"use client";

import React, { useState } from "react";
import {
  Dialog,
  DialogPortal,
  DialogBackdrop,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogClose,
  Autocomplete,
  Button,
  Alert,
  Input,
  Checkbox,
  Tag,
} from "@/ui";
import isEmailValid from "@lib/helpers/isEmailValid";
import toast from "react-hot-toast";
import useCreateContact from "@lib/contacts/useCreateContact";
import useTags from "@lib/tags/useTags";
import useUpdateContact from "@lib/contacts/useUpdateContact";
import useContactCompanies from "@lib/contacts/useContactCompanies";
import { TAG_TYPE } from "@lib/enums";
import styles from "./ContactDetailsDialog.module.css";

interface ContactMethods {
  email: boolean;
  sms: boolean;
  linkedin: boolean;
}

interface ContactTag {
  id: number;
  name: string;
}

interface ContactFormState {
  id: number;
  first_name: string;
  last_name: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  job_title: string;
  linkedin_url: string;
  contact_methods: ContactMethods;
}

interface ContactDetailsDialogProps {
  /** The contact being edited, or null to add a new one. */
  contactToEdit: EditableContact;
  onClose: () => void;
  open: boolean;
  recruiterId: number;
}

type EditableContact = (Partial<ContactFormState> & { tags?: ContactTag[] }) | null;

const TAGS_INPUT_ID = "contact-tags";

const INITIAL_CONTACT: ContactFormState = {
  id: 0,
  first_name: "",
  last_name: "",
  name: "",
  email: "",
  phone: "",
  company: "",
  job_title: "",
  linkedin_url: "",
  contact_methods: {
    email: true,
    sms: false,
    linkedin: false,
  },
};

/** The editable form state for a contact, or a blank form when adding. */
function contactState(contactToEdit: EditableContact): ContactFormState {
  if (!contactToEdit) {
    return INITIAL_CONTACT;
  }

  return {
    ...INITIAL_CONTACT,
    ...contactToEdit,
    contact_methods: {
      ...INITIAL_CONTACT.contact_methods,
      ...contactToEdit.contact_methods,
    },
  };
}

function tagState(contactToEdit: EditableContact): string[] {
  return contactToEdit?.tags?.map((tag) => tag.name) ?? [];
}

export default function ContactDetailsDialog({
  contactToEdit,
  onClose,
  open,
  recruiterId,
}: ContactDetailsDialogProps) {
  const [formError, setFormError] = useState("");
  const isEdit = contactToEdit !== null;
  const [contact, setContact] = useState<ContactFormState>(() => contactState(contactToEdit));
  // Tags are held as plain names: that is what the API accepts on save, and it
  // sidesteps matching a saved tag's id against the option list.
  const [tagNames, setTagNames] = useState<string[]>(() => tagState(contactToEdit));
  const create = useCreateContact();
  const update = useUpdateContact();
  const { companies } = useContactCompanies();

  // Sync the incoming contact prop into editable local state when it changes.
  // Uses the store-previous-prop-in-state pattern (React's replacement for a
  // prop-syncing effect) to avoid cascading renders.
  const [prevContactToEdit, setPrevContactToEdit] = useState(contactToEdit);
  if (prevContactToEdit !== contactToEdit) {
    setPrevContactToEdit(contactToEdit);
    setContact(contactState(contactToEdit));
    setTagNames(tagState(contactToEdit));
  }

  const { tags, isLoading: tagsIsLoading, invalidate } = useTags({
    page: 1,
    sortBy: { property: "name", direction: "asc" },
    search: "",
    filters: { with_default: true, unique: "slug", type: TAG_TYPE.CONTACT },
  });

  // The list mixes the recruiter's tags with the system defaults, so names can
  // repeat; already-picked tags drop out rather than offering a no-op.
  const tagOptions = React.useMemo(() => {
    const picked = new Set(tagNames.map((name) => name.toLowerCase()));
    const seen = new Set<string>();

    return ((tags ?? []) as ContactTag[]).reduce<ContactTag[]>((options, tag) => {
      const key = tag.name?.trim().toLowerCase();
      if (!key || picked.has(key) || seen.has(key)) {
        return options;
      }
      seen.add(key);
      options.push(tag);
      return options;
    }, []);
  }, [tags, tagNames]);

  const addTag = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) {
      return;
    }
    setTagNames((prev) =>
      prev.some((tag) => tag.toLowerCase() === trimmed.toLowerCase())
        ? prev
        : [...prev, trimmed]
    );
  };

  const removeTag = (name: string) => {
    setTagNames((prev) => prev.filter((tag) => tag !== name));
  };

  const handleUpdateOrAdd = () => {
    if (!contact.first_name || !contact.last_name) {
      setFormError("Please complete all required fields");
      return;
    }
    if (contact.email && !isEmailValid(contact.email.trim())) {
      setFormError("Please enter a valid email address");
      return;
    }

    const data = {
      ...contact,
      tags: tagNames,
      recruiter_id: recruiterId,
    };

    const onSuccess = (message: string) => () => {
      toast.success(message);
      invalidate();
      setContact(INITIAL_CONTACT);
      setTagNames([]);
      onClose();
    };

    const onError = (error: Error) => {
      const errors =
        (error as { response?: { data?: { errors?: Record<string, string[]> } } })
          .response?.data?.errors ?? {};
      let errorMessage = "";
      Object.entries(errors).forEach(([, value]) => {
        errorMessage += value.join("\n");
      });
      setFormError(errorMessage || error.message);
    };

    if (isEdit) {
      update.mutate(data, { onSuccess: onSuccess("Contact updated"), onError });
      return;
    }

    create.mutate(data, { onSuccess: onSuccess("Contact added"), onError });
  };

  const validateEmail = (email: string) => {
    if (!email) return;
    setFormError(isEmailValid(email.trim()) ? "" : "Please enter a valid email address");
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen: boolean) => { if (!isOpen) onClose(); }}>
      <DialogPortal>
        <DialogBackdrop />
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>{isEdit ? "Edit" : "Add"} contact</DialogTitle>
            <DialogClose />
          </DialogHeader>
          <DialogBody>
            {formError && (
              <Alert variant="error" className={styles.error}>{formError}</Alert>
            )}
            <div className={styles.form}>
              <Autocomplete<string>
                label="Company"
                placeholder="e.g. Acme Corp"
                items={companies}
                inputValue={contact.company}
                onInputChange={(value) => setContact((prev) => ({ ...prev, company: value }))}
                onSelect={(company) => setContact((prev) => ({ ...prev, company }))}
                allowCustomValue
                onCreate={(company) => setContact((prev) => ({ ...prev, company }))}
                createOptionLabel={(value) => (
                  <span>
                    Add new company <strong>&ldquo;{value}&rdquo;</strong>
                  </span>
                )}
                clearOnSelect={false}
                emptyText="No matching companies"
              />
              <Input
                label="First name"
                required
                fullWidth
                autoFocus
                value={contact.first_name}
                onChange={(e) => setContact((prev) => ({ ...prev, first_name: e.target.value }))}
              />
              <Input
                label="Last name"
                required
                fullWidth
                value={contact.last_name}
                onChange={(e) => setContact((prev) => ({ ...prev, last_name: e.target.value }))}
              />
              <Input
                label="Email"
                fullWidth
                type="email"
                value={contact.email}
                onBlur={(e) => validateEmail(e.target.value)}
                onChange={(e) => setContact((prev) => ({ ...prev, email: e.target.value }))}
              />
              <Input
                label="Phone"
                fullWidth
                value={contact.phone}
                onChange={(e) => setContact((prev) => ({ ...prev, phone: e.target.value }))}
              />
              <Input
                label="Job title"
                fullWidth
                value={contact.job_title}
                onChange={(e) => setContact((prev) => ({ ...prev, job_title: e.target.value }))}
              />
              <Input
                label="LinkedIn URL"
                fullWidth
                value={contact.linkedin_url}
                onChange={(e) => setContact((prev) => ({ ...prev, linkedin_url: e.target.value }))}
              />
              <div>
                <span className={styles.fieldLabel}>Preferred contact methods</span>
                <div className={styles.contactMethods}>
                  <Checkbox
                    label="Email"
                    checked={contact.contact_methods?.email ?? false}
                    onChange={(checked) => setContact((prev) => ({ ...prev, contact_methods: { ...prev.contact_methods, email: checked } }))}
                  />
                  <Checkbox
                    label="SMS"
                    checked={contact.contact_methods?.sms ?? false}
                    onChange={(checked) => setContact((prev) => ({ ...prev, contact_methods: { ...prev.contact_methods, sms: checked } }))}
                  />
                  <Checkbox
                    label="LinkedIn"
                    checked={contact.contact_methods?.linkedin ?? false}
                    onChange={(checked) => setContact((prev) => ({ ...prev, contact_methods: { ...prev.contact_methods, linkedin: checked } }))}
                  />
                </div>
              </div>
              <div>
                {/* The label sits above the chips, so it belongs to the field
                    rather than to the Autocomplete's own input. */}
                <label className={styles.fieldLabel} htmlFor={TAGS_INPUT_ID}>
                  Tags
                </label>
                {tagNames.length > 0 && (
                  <div className={styles.tagList}>
                    {tagNames.map((name) => (
                      <Tag
                        key={name}
                        size="sm"
                        color="neutral"
                        removable
                        onRemove={() => removeTag(name)}
                      >
                        {name}
                      </Tag>
                    ))}
                  </div>
                )}
                <Autocomplete<ContactTag>
                  id={TAGS_INPUT_ID}
                  placeholder="Search your tags or add a new one..."
                  items={tagOptions}
                  getOptionLabel={(tag) => tag.name}
                  getOptionKey={(tag) => tag.id}
                  onSelect={(tag) => addTag(tag.name)}
                  allowCustomValue
                  onCreate={addTag}
                  createOptionLabel={(value) => (
                    <span>
                      Create new tag <strong>&ldquo;{value}&rdquo;</strong>
                    </span>
                  )}
                  loading={tagsIsLoading}
                  loadingText="Loading tags…"
                  emptyText="No matching tags"
                />
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button
              variant="primary"
              onClick={handleUpdateOrAdd}
              disabled={create.isPending || update.isPending}
            >
              {create.isPending || update.isPending
                ? isEdit ? "Updating..." : "Adding..."
                : isEdit ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
